/**
 * CLI handlers for `nexus migrate` commands.
 *
 * Migration file management (create, dev, deploy, status, down, reset).
 */
import { readFileSync, existsSync } from 'node:fs'
import { join } from 'node:path'
import { pathToFileURL } from 'node:url'
import Table from 'cli-table3'
import { consola } from 'consola'
import { getDatabaseType } from '@kernel/config/database.js'
import { loadCoreModules, registerPlugin, registerModule, getOrderedModules, getOrderedModulesInternal, topologicalSortPlugins } from '@kernel/engine/index.js'
import { validateWorkflowConfig, injectWorkflowField } from '@kernel/runtime/workflow/inject-workflow-field.js'
import { loadNexusConfig, extractPluginManifest } from '@kernel/config/load-config.js'
import { initCli } from './shared.js'
import { getProjectPath } from '@kernel/config/paths.js'
import { getDb } from '@kernel/db/connection.js'
import { ensureMigrationTables } from '@kernel/db/ensure-system-tables.js'
import { generateMigrationForSource, devMigration, detectSchemaDrift, detectDefaultScope, formatDriftMessage } from '@kernel/db/migration-generator.js'
import {
  runMigrations,
  rollbackLastBatch,
  rollbackByName,
  rollbackAll,
  showMigrationStatus,
  resetDatabase,
  undoLastMigration,
} from '@kernel/db/migration-runner.js'
import type { MigrationSourceId } from '@kernel/db/migration-sources.js'

/**
 * Convert MigrationScope to MigrationSourceId for runner functions.
 * 'all' becomes undefined (no filter), other values pass through.
 */
function scopeToSourceId(scope: string): MigrationSourceId | undefined {
  if (!scope || scope === 'all') return undefined
  return scope as MigrationSourceId
}

/**
 * If running from a plugin directory (package.json name contains `nexus-plugin-`),
 * dynamically import the plugin's entry point and register it.
 * This enables `npx nexus migrate dev` from within a plugin workspace.
 */
async function loadSelfPlugin(): Promise<void> {
  const projectPath = getProjectPath()
  const pkgPath = join(projectPath, 'package.json')

  if (!existsSync(pkgPath)) return

  try {
    const pkg = JSON.parse(readFileSync(pkgPath, 'utf-8'))
    const pkgName = pkg?.name as string | undefined

    if (!pkgName || !/nexus-plugin-/.test(pkgName)) return

    // Try to import the plugin's entry point.
    // Prefer src/index.ts over dist/index.js so that `migrate:dev` always
    // sees the latest entity definitions without requiring a rebuild first.
    // When running from the compiled CLI (dist/cli.js), plain Node.js can't
    // import .ts natively — register tsx as an ESM loader via node:module.
    const srcEntry = join(projectPath, 'src', 'index.ts')
    const distEntry = join(projectPath, 'dist', 'index.js')

    // Try src/index.ts first (most up-to-date definitions)
    if (existsSync(srcEntry)) {
      try {
        // Use tsx/esm/api to import .ts files from plain Node.js context.
        // tsx is marked as external in tsup.config.ts so it resolves from
        // node_modules at runtime (not bundled).
        const { tsImport } = await import('tsx/esm/api')
        const mod = await tsImport(
          pathToFileURL(srcEntry).href,
          import.meta.url,
        ) as Record<string, unknown>
        const manifest = extractPluginManifest(mod)

        if (manifest) {
          if (!manifest.migrationsDir) {
            manifest.migrationsDir = join(projectPath, 'migrations')
          }
          registerPlugin(manifest)
          return
        }
      } catch (err) {
        // tsx not available or src import failed — fall through to dist
        console.error(`  ⚠ Failed to load plugin src/index.ts: ${(err as Error).message}`)
      }
    }

    // Fallback: dist/index.js (may be stale if not rebuilt)
    if (existsSync(distEntry)) {
      try {
        const mod = await import(pathToFileURL(distEntry).href)
        const manifest = extractPluginManifest(mod)

        if (manifest) {
          if (!manifest.migrationsDir) {
            manifest.migrationsDir = join(projectPath, 'migrations')
          }
          registerPlugin(manifest)
          return
        }
      } catch (err) {
        // dist import also failed
        console.error(`  ⚠ Failed to load plugin dist/index.js: ${(err as Error).message}`)
      }
    }
  } catch {
    // Ignore errors — not a plugin directory
  }
}

/**
 * Load core modules, user plugins, and development plugins.
 * CLI registers ALL plugins without shouldLoadPlugin() filter
 * because migrations must cover all defined tables (even disabled plugins).
 *
 * When running from a plugin directory, also loads the plugin itself.
 */
export async function loadModulesForMigration(): Promise<void> {
  loadCoreModules()

  // If CWD is a plugin directory, register the plugin itself first
  await loadSelfPlugin()

  const config = await loadNexusConfig()

  if (config.plugins?.length) {
    const sorted = topologicalSortPlugins(config.plugins)
    for (const plugin of sorted) {
      registerPlugin(plugin)
    }
  }

  const coreNames = new Set(getOrderedModules().map((m) => m.name))
  for (const mod of config.modules ?? []) {
    if (!coreNames.has(mod.name)) {
      registerModule(mod, { source: 'standalone' })
    }
  }

  // Inject workflow fields into entity definitions so the migration generator sees them.
  // This mirrors the logic in module-routes.ts section 1a, which only runs at runtime boot.
  for (const mod of getOrderedModulesInternal()) {
    for (const def of mod.definitions ?? []) {
      const workflow = (def as { workflow?: import('@gzl10/nexus-sdk').WorkflowConfig }).workflow
      if (!workflow) continue
      const entityName = ('table' in def ? (def as { table: string }).table : undefined) ?? mod.name
      validateWorkflowConfig(workflow, entityName)
      const fields = (def as { fields: Record<string, unknown> }).fields
      injectWorkflowField(fields, workflow)
    }
  }
}

export async function handleCreate(name: string): Promise<void> {
  initCli()
  await loadModulesForMigration()

  const scope = detectDefaultScope()
  const db = getDb()

  try {
    await ensureMigrationTables(db)
    await generateMigrationForSource(name, scope)
  } catch (err) {
    console.error('Migration generation failed:', err)
    process.exit(1)
  } finally {
    await db.destroy()
    process.exit(0)
  }
}

export async function handleDev(name: string | undefined): Promise<void> {
  initCli()
  await loadModulesForMigration()

  const db = getDb()

  try {
    await ensureMigrationTables(db)
    await devMigration(name)
  } catch (err) {
    console.error('Migration dev failed:', err)
    process.exit(1)
  } finally {
    await db.destroy()
    process.exit(0)
  }
}

export async function handleUp(): Promise<void> {
  initCli()
  await loadModulesForMigration()

  const db = getDb()

  try {
    consola.start(`Running migrations [${getDatabaseType()}]`)
    await ensureMigrationTables(db)
    await runMigrations()
    consola.success('Migrations completed successfully')
  } catch (err) {
    consola.error('Migration failed:', err)
    process.exit(1)
  } finally {
    await db.destroy()
    process.exit(0)
  }
}

export async function handleDeploy(options: { checkDrift?: boolean }): Promise<void> {
  initCli()
  await loadModulesForMigration()

  const db = getDb()

  try {
    await ensureMigrationTables(db)

    const migrationFiles = await import('@kernel/db/migration-runner.js').then((m) => m.loadAllMigrationFiles())
    const executed = await db('_nexus_migrations')
      .where({ status: 'completed' })
      .select('name')
      .then((rows) => new Set(rows.map((r) => r.name)))

    const pending = migrationFiles.filter((m) => !executed.has(m.name))

    if (pending.length === 0) {
      consola.info('No pending migrations — database is up to date')
    } else {
      consola.start(`Deploying ${pending.length} migration(s) [${getDatabaseType()}]`)
      await runMigrations()
      consola.success(`${pending.length} migration(s) deployed successfully`)
    }

    if (options.checkDrift) {
      consola.start('Checking for schema drift...')
      const drift = await detectSchemaDrift()

      if (drift) {
        consola.error(formatDriftMessage(drift))
        consola.error('Schema drift detected. Run "nexus migrate dev" to fix.')
        process.exit(1)
      } else {
        consola.success('No schema drift — database matches entity definitions.')
      }
    }
  } catch (err) {
    consola.error('Migration deploy failed:', err)
    process.exit(1)
  } finally {
    await db.destroy()
    process.exit(0)
  }
}

export async function handleDown(options?: { name?: string }): Promise<void> {
  initCli()
  await loadModulesForMigration()

  const scope = detectDefaultScope()
  const db = getDb()

  try {
    await ensureMigrationTables(db)

    if (options?.name) {
      consola.start(`Rolling back migration: ${options.name}`)
      await rollbackByName(options.name)
    } else {
      const sourceId = scopeToSourceId(scope)
      consola.start(`Rolling back last batch [${scope}]`)
      await rollbackLastBatch(undefined, sourceId)
    }

    consola.success('Rollback completed successfully')
  } catch (err) {
    consola.error('Rollback failed:', err)
    process.exit(1)
  } finally {
    await db.destroy()
    process.exit(0)
  }
}

export async function handleUndo(options?: { force?: boolean }): Promise<void> {
  if (process.env['NODE_ENV'] === 'production') {
    consola.warn('Cannot undo migrations in production.')
    return
  }

  initCli()
  await loadModulesForMigration()

  const scope = detectDefaultScope()
  const sourceId = scopeToSourceId(scope)
  const db = getDb()

  try {
    await ensureMigrationTables(db)

    // Preview: find the migration that will be undone (best-effort — the
    // actual undo inside undoLastMigration() re-queries under lock, so a
    // race between preview and execution is harmless).
    const previewQuery = db('_nexus_migrations')
      .where({ status: 'completed' })
    if (sourceId) previewQuery.where({ source: sourceId })
    const target = await previewQuery
      .orderBy('executed_at', 'desc')
      .orderBy('batch', 'desc')
      .first()

    if (!target) {
      consola.info(`No completed migrations to undo [${scope}]`)
      process.exit(0)
    }

    consola.warn(`This will rollback AND DELETE migration: ${target.name}`)

    if (!options?.force) {
      const readline = await import('node:readline')
      const rl = readline.createInterface({ input: process.stdin, output: process.stdout })

      const answer = await new Promise<string>((resolve) => {
        rl.question('Continue? (y/N) ', resolve)
      })
      rl.close()

      if (answer.toLowerCase() !== 'y') {
        consola.info('Aborted')
        process.exit(0)
      }
    }

    const result = await undoLastMigration(db, sourceId)

    if (result) {
      consola.success(`Undone: ${result.name}`)
    }
  } catch (err) {
    consola.error('Undo failed:', err)
    process.exit(1)
  } finally {
    await db.destroy()
    process.exit(0)
  }
}

export async function handleStatus(): Promise<void> {
  initCli()
  await loadModulesForMigration()

  const db = getDb()

  try {
    await ensureMigrationTables(db)
    const status = await showMigrationStatus()

    if (status.totalFiles === 0) {
      consola.info('No migration files found')
    } else {
      for (const [source, rows] of status.bySource) {
        console.log(`\n  ${source}`)
        const table = new Table({ head: ['Migration', 'Status', 'Batch', 'Time'] })
        for (const row of rows) {
          const icon = row.status === 'completed' ? '✓' : row.status === 'failed' ? '✗' : '…'
          table.push([
            row.name,
            `${icon} ${row.status}`,
            row.batch != null ? String(row.batch) : '–',
            row.executionTime != null ? `${row.executionTime}ms` : '–',
          ])
        }
        console.log(table.toString())
      }
      console.log(`\n  Total: ${status.total}/${status.totalFiles} completed`)
    }

    // Always check for schema drift
    console.log('')
    consola.start('Checking for schema drift...')
    const drift = await detectSchemaDrift()

    if (drift) {
      consola.warn(formatDriftMessage(drift))
    } else {
      consola.success('No schema drift — database matches entity definitions.')
    }
  } catch (err) {
    consola.error('Failed to show status:', err)
    process.exit(1)
  } finally {
    await db.destroy()
    process.exit(0)
  }
}

export async function handleReset(options?: { force?: boolean }): Promise<void> {
  if (process.env['NODE_ENV'] === 'production') {
    consola.warn('Cannot reset database in production.')
    return
  }

  initCli()
  await loadModulesForMigration()

  const scope = detectDefaultScope()
  const sourceId = scopeToSourceId(scope)
  const db = getDb()

  try {
    consola.warn(`Resetting database [${scope}] — rollback all + re-run`)

    if (!options?.force) {
      const readline = await import('node:readline')
      const rl = readline.createInterface({ input: process.stdin, output: process.stdout })
      const answer = await new Promise<string>((resolve) => {
        rl.question('Continue? (y/N) ', resolve)
      })
      rl.close()
      if (answer.toLowerCase() !== 'y') {
        consola.info('Aborted')
        process.exit(0)
      }
    }

    await ensureMigrationTables(db)
    await resetDatabase(undefined, sourceId)
    consola.success('Database reset complete')
  } catch (err) {
    consola.error('Reset failed:', err)
    process.exit(1)
  } finally {
    await db.destroy()
    process.exit(0)
  }
}

export async function handleDrop(options?: { force?: boolean }): Promise<void> {
  if (process.env['NODE_ENV'] === 'production') {
    consola.warn('Cannot drop migrations in production.')
    return
  }

  initCli()
  await loadModulesForMigration()

  const scope = detectDefaultScope()
  const sourceId = scopeToSourceId(scope)!
  const db = getDb()

  try {
    await ensureMigrationTables(db)

    consola.warn(`This will DROP ALL migrations for source: ${sourceId}`)

    if (!options?.force) {
      const readline = await import('node:readline')
      const rl = readline.createInterface({ input: process.stdin, output: process.stdout })
      const answer = await new Promise<string>((resolve) => {
        rl.question('Continue? (y/N) ', resolve)
      })
      rl.close()
      if (answer.toLowerCase() !== 'y') {
        consola.info('Aborted')
        process.exit(0)
      }
    }

    consola.start(`Dropping all migrations for source: ${sourceId}`)
    await rollbackAll(db, sourceId)
    consola.success(`All migrations dropped for source: ${sourceId}`)
  } catch (err) {
    consola.error('Drop failed:', err)
    process.exit(1)
  } finally {
    await db.destroy()
    process.exit(0)
  }
}

